import React, { useState, useEffect } from "react";
import * as Sentry from '@sentry/browser';
import Api from "../../../Api";
import Modal from "../../Utils/Modal";
import Loader from "../../Utils/Loader";
import classes from "./styles.module.css";
import loadRegistryRequests from "./loadRegistryRequests";
import { formatAmount, normalizeItem, STATUS_NAMES, REGISTRY_STATUS } from "./CrmRegistryModalUtils";

function formatDate(dateStr) {
    if (!dateStr) return "";
    var d = new Date(dateStr);
    return d.toLocaleDateString("ru-RU") + " " + d.toLocaleTimeString("ru-RU", { hour: "2-digit", minute: "2-digit" });
}

function CrmRegistryModal(props) {
    var registryId = props.registryId;
    var registryInfo = props.registryInfo;
    var token = props.token;
    var onClose = props.onClose;
    var onComplete = props.onComplete;
    var onMessage = props.onMessage || function () {};
    var payoutType = props.payoutType || "card";
    var isSbp = payoutType === "sbp";

    var _requests = useState([]);
    var requests = _requests[0];
    var setRequests = _requests[1];

    var _loading = useState(false);
    var loading = _loading[0];
    var setLoading = _loading[1];

    var _uploading = useState(false);
    var uploading = _uploading[0];
    var setUploading = _uploading[1];

    var _uploadResult = useState(null);
    var uploadResult = _uploadResult[0];
    var setUploadResult = _uploadResult[1];

    // Предпросмотр отчёта: что произойдёт, если применить выбранный файл.
    // Файл держим в состоянии — применяем ровно тот, цифры по которому показали.
    var _preview = useState(null);
    var preview = _preview[0];
    var setPreview = _preview[1];

    var _pendingFile = useState(null);
    var pendingFile = _pendingFile[0];
    var setPendingFile = _pendingFile[1];

    // Чекбоксы для выбора заявок
    var _checkedIds = useState([]);
    var checkedIds = _checkedIds[0];
    var setCheckedIds = _checkedIds[1];

    // Состояние для "Реестр из неуспешных"
    var _showRetryModal = useState(false);
    var showRetryModal = _showRetryModal[0];
    var setShowRetryModal = _showRetryModal[1];

    var _retryRegistryName = useState("");
    var retryRegistryName = _retryRegistryName[0];
    var setRetryRegistryName = _retryRegistryName[1];

    var _retrying = useState(false);
    var retrying = _retrying[0];
    var setRetrying = _retrying[1];

    // Подтверждение удаления из реестра
    var _showRemoveConfirm = useState(false);
    var showRemoveConfirm = _showRemoveConfirm[0];
    var setShowRemoveConfirm = _showRemoveConfirm[1];

    // Подтверждение расформирования
    var _showDisbandConfirm = useState(false);
    var showDisbandConfirm = _showDisbandConfirm[0];
    var setShowDisbandConfirm = _showDisbandConfirm[1];

    var _disbanding = useState(false);
    var disbanding = _disbanding[0];
    var setDisbanding = _disbanding[1];

    // Остаток в новый реестр
    var _showSplitModal = useState(false);
    var showSplitModal = _showSplitModal[0];
    var setShowSplitModal = _showSplitModal[1];

    var _splitRegistryName = useState("");
    var splitRegistryName = _splitRegistryName[0];
    var setSplitRegistryName = _splitRegistryName[1];

    var _splitting = useState(false);
    var splitting = _splitting[0];
    var setSplitting = _splitting[1];

    // Ref для скрытого file input
    var fileInputRef = React.useRef(null);

    // Загрузка заявок реестра
    useEffect(function () {
        if (!registryId) return;

        var active = true;
        setLoading(true);
        setRequests([]);
        setUploadResult(null);
        setCheckedIds([]);

        loadRegistryRequests(new Api({ token: token }), registryId).then(function (rows) {
            if (!active) return;
            var items = rows.map(normalizeItem);
            setRequests(items);
        }).catch(function (err) {
            if (!active) return;
            Sentry.captureException(err);
            setRequests([]);
            onMessage({ type: "error", message: "Не удалось загрузить все заявки реестра. Откройте реестр повторно" });
        }).then(function () {
            if (active) setLoading(false);
        });
        return function () { active = false; };
    }, [registryId, token]);

    if (!registryId) return null;

    // Сумма заявок
    var totalAmount = 0;
    requests.forEach(function (r) {
        totalAmount += Number(r.amount) || 0;
    });

    // Неуспешные заявки (статус 6 = Ошибка)
    var failedRequests = requests.filter(function (r) {
        return Number(r.status) === 6;
    });
    var hasFailedRequests = failedRequests.length > 0;

    // Выплаченные (5) и ещё ожидающие ответа (3 «В реестре» + 4 «В работе»).
    // Без этой разбивки по реестру не понять, сколько выплат ещё не отвечено:
    // видно было только «Заявок» и «Неуспешных».
    var paidRequests = requests.filter(function (r) {
        return Number(r.status) === 5;
    });
    var awaitingRequests = requests.filter(function (r) {
        var s = Number(r.status);
        return s === 3 || s === 4;
    });
    var paidAmount = 0;
    paidRequests.forEach(function (r) {
        paidAmount += Number(r.amount) || 0;
    });

    // Заявки в статусе "В реестре" (3) — только их можно вытащить
    var inRegistryRequests = requests.filter(function (r) {
        return Number(r.status) === 3;
    });
    var canDisband = inRegistryRequests.length > 0;

    // Выбранные заявки, которые в статусе "В реестре"
    var checkedInRegistry = checkedIds.filter(function (id) {
        var req = requests.find(function (r) { return r.id === id; });
        return req && Number(req.status) === 3;
    });

    // Незавершённые заявки (IN_REGISTRY=3 или PROCESSING=4)
    var remainingRequests = requests.filter(function (r) {
        var s = Number(r.status);
        return s === 3 || s === 4;
    });

    // Завершённые заявки (SUCCESS=5 или FAILED=6)
    var finishedRequests = requests.filter(function (r) {
        var s = Number(r.status);
        return s === 5 || s === 6;
    });

    // Показывать "Остаток в новый реестр" — есть незавершённые И есть завершённые (частично обработан)
    var canSplitRemaining = remainingRequests.length > 0 && finishedRequests.length > 0;

    // Все ли заявки в статусе SUCCESS (5) — скрываем кнопку загрузки отчёта
    var allSuccess = requests.length > 0 && requests.every(function (r) {
        return Number(r.status) === 5;
    });

    // Все ли выбраны
    var allChecked = requests.length > 0 && checkedIds.length === requests.length;

    function handleCheckAll(e) {
        if (e.target.checked) {
            setCheckedIds(requests.map(function (r) { return r.id; }));
        } else {
            setCheckedIds([]);
        }
    }

    function handleCheckOne(id, checked) {
        if (checked) {
            setCheckedIds(function (prev) { return prev.concat([id]); });
        } else {
            setCheckedIds(function (prev) { return prev.filter(function (x) { return x !== id; }); });
        }
    }

    // Скачать Excel. format: 'yupay' — реестр в формате Юпэй, без него — обычный
    function handleDownloadExcel(format) {
        setLoading(true);
        new Api({ token: token }).downloadCrmRegistryExcel(registryId, format)
            .then(function (blob) {
                var url = window.URL.createObjectURL(blob);
                var a = document.createElement("a");
                a.href = url;
                a.download = "crm_registry_" + registryId + ".xlsx";
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
                window.URL.revokeObjectURL(url);
            })
            .catch(function (e) {
                Sentry.captureException(e);
                // responseType: 'blob' — при ошибке response.data может быть Blob с JSON
                if (e.response && e.response.data instanceof Blob) {
                    e.response.data.text().then(function (text) {
                        try {
                            var data = JSON.parse(text);
                            alert(data.message);
                        } catch (parseErr) {
                            onMessage({ type: "error", message: "Ошибка скачивания: " + (e.message || "Неизвестная ошибка") });
                        }
                    });
                } else if (e.responseData) {
                    alert(e.responseData.message);
                } else {
                    onMessage({ type: "error", message: "Ошибка скачивания: " + (e.message || "Неизвестная ошибка") });
                }
            })
            .then(function () {
                setLoading(false);
            });
    }

    // Загрузить отчёт — по клику на кнопку открываем file picker
    function handleUploadClick() {
        if (fileInputRef.current) {
            fileInputRef.current.value = "";
            fileInputRef.current.click();
        }
    }

    // Файл выбран — сначала показываем предпросмотр, ничего не записывая
    function handleFileSelected(e) {
        var file = e.target.files && e.target.files[0];
        if (!file) return;

        setUploading(true);
        setUploadResult(null);
        setPreview(null);
        setPendingFile(null);

        var formData = new FormData();
        formData.append("file", file);

        new Api({ token: token }).previewCrmRegistryReport(registryId, formData)
            .then(function (result) {
                setPreview(result);
                setPendingFile(file);
            })
            .catch(function (err) {
                Sentry.captureException(err);
                var msg = (err.response && err.response.data && err.response.data.message) || err.message || "Ошибка";
                onMessage({ type: "error", message: "Не удалось прочитать отчёт: " + msg });
            })
            .then(function () {
                setUploading(false);
            });
    }

    // Отменить предпросмотр — файл не применяется
    function handlePreviewCancel() {
        setPreview(null);
        setPendingFile(null);
        if (fileInputRef.current) fileInputRef.current.value = "";
    }

    // Применить показанный отчёт
    function handlePreviewApply() {
        if (!pendingFile) return;

        setUploading(true);

        var formData = new FormData();
        formData.append("file", pendingFile);

        new Api({ token: token }).uploadCrmRegistryReport(registryId, formData)
            .then(function (result) {
                setUploadResult(result);
                // Перезагружаем список заявок
                return loadRegistryRequests(new Api({ token: token }), registryId);
            })
            .then(function (rows) {
                var items = rows.map(normalizeItem);
                setRequests(items);
                setCheckedIds([]);
                setPreview(null);
                setPendingFile(null);
                if (fileInputRef.current) fileInputRef.current.value = "";
                if (onComplete) onComplete();
            })
            .catch(function (e) {
                Sentry.captureException(e);
                var msg = (e.response && e.response.data && e.response.data.message) || e.message || "Ошибка";
                onMessage({ type: "error", message: "Ошибка загрузки: " + msg });
            })
            .then(function () {
                setUploading(false);
            });
    }

    // Убрать выбранные из реестра — шаг 1: проверить и показать подтверждение
    function handleRemoveFromRegistry() {
        if (checkedInRegistry.length === 0) {
            onMessage({ type: "warning", message: "Выберите заявки в статусе \"В реестре\" для удаления" });
            return;
        }
        setShowRemoveConfirm(true);
    }

    // Убрать выбранные из реестра — шаг 2: подтверждённое удаление
    function handleRemoveConfirmed() {
        setShowRemoveConfirm(false);
        setLoading(true);
        new Api({ token: token }).removeFromCrmRegistry(checkedInRegistry)
            .then(function (result) {
                var msg = "Убрано из реестра: " + (result.removed || 0);
                if (result.errors && result.errors.length > 0) {
                    msg += ". Ошибки: " + result.errors.map(function (e) { return "#" + e.id + ": " + e.error; }).join(", ");
                }
                onMessage({ type: result.errors && result.errors.length > 0 ? "warning" : "success", message: msg });

                // Перезагружаем
                return loadRegistryRequests(new Api({ token: token }), registryId);
            })
            .then(function (rows) {
                var items = rows.map(normalizeItem);
                setRequests(items);
                setCheckedIds([]);
                if (onComplete) onComplete();

                // Если заявок не осталось — закрываем модалку
                if (items.length === 0) {
                    onClose();
                }
            })
            .catch(function (e) {
                Sentry.captureException(e);
                var msg = (e.response && e.response.data && e.response.data.message) || e.message || "Ошибка";
                onMessage({ type: "error", message: "Ошибка: " + msg });
            })
            .then(function () {
                setLoading(false);
            });
    }

    // Расформировать реестр
    function handleDisbandRegistry() {
        setShowDisbandConfirm(false);
        setDisbanding(true);

        new Api({ token: token }).disbandCrmRegistry(registryId)
            .then(function (result) {
                onMessage({ type: "success", message: "Реестр расформирован. Заявок возвращено: " + (result.requestsReturned || 0) });
                if (onComplete) onComplete();
                onClose();
            })
            .catch(function (e) {
                Sentry.captureException(e);
                var msg = (e.response && e.response.data && e.response.data.message) || e.message || "Ошибка";
                onMessage({ type: "error", message: "Ошибка: " + msg });
            })
            .then(function () {
                setDisbanding(false);
            });
    }

    // Реестр из неуспешных — два шага: вернуть в ожидание + создать реестр
    function handleRetryFailed() {
        if (!retryRegistryName) {
            onMessage({ type: "warning", message: "Укажите название реестра" });
            return;
        }

        var failedIds = failedRequests.map(function (r) { return r.id; });
        if (failedIds.length === 0) return;

        var api = new Api({ token: token });
        setRetrying(true);

        // Шаг 1: вернуть неуспешные в ожидание
        api.moveBackToAwaitingCrmPayoutRequests(failedIds)
            .then(function () {
                // Шаг 2: создать новый реестр из них
                return api.formCrmRegistry(failedIds, retryRegistryName, null);
            })
            .then(function (result) {
                onMessage({ type: "success", message: "Реестр #" + result.registryId + " создан. Заявок: " + result.requestCount });
                setShowRetryModal(false);
                setRetryRegistryName("");
                // Перезагружаем текущий реестр
                return loadRegistryRequests(new Api({ token: token }), registryId);
            })
            .then(function (rows) {
                var items = rows.map(normalizeItem);
                setRequests(items);
                setCheckedIds([]);
                if (onComplete) onComplete();
            })
            .catch(function (e) {
                Sentry.captureException(e);
                var msg = (e.response && e.response.data && e.response.data.message) || e.message || "Ошибка";
                onMessage({ type: "error", message: "Ошибка: " + msg });
            })
            .then(function () {
                setRetrying(false);
            });
    }

    // Остаток в новый реестр
    function handleSplitRemaining() {
        if (!splitRegistryName) {
            onMessage({ type: "warning", message: "Укажите название реестра" });
            return;
        }

        setSplitting(true);
        new Api({ token: token }).splitCrmRegistryRemaining(registryId, splitRegistryName)
            .then(function (result) {
                onMessage({ type: "success", message: "Создан реестр #" + result.newRegistryId + ". Перенесено заявок: " + result.movedCount });
                setShowSplitModal(false);
                setSplitRegistryName("");
                // Перезагружаем текущий реестр
                return loadRegistryRequests(new Api({ token: token }), registryId);
            })
            .then(function (rows) {
                var items = rows.map(normalizeItem);
                setRequests(items);
                setCheckedIds([]);
                if (onComplete) onComplete();
            })
            .catch(function (e) {
                Sentry.captureException(e);
                var msg = (e.response && e.response.data && e.response.data.message) || e.message || "Ошибка";
                onMessage({ type: "error", message: "Ошибка: " + msg });
            })
            .then(function () {
                setSplitting(false);
            });
    }

    // Информация о реестре из props
    var regName = (registryInfo && registryInfo.name) || ("Реестр #" + registryId);
    var regStatus = registryInfo ? Number(registryInfo.status) : null;
    var regStatusInfo = regStatus !== null ? (REGISTRY_STATUS[regStatus] || null) : null;
    var regDate = registryInfo ? registryInfo.date : null;

    var title = regName;

    return (
        <Modal isOpen={true} onClose={onClose} title={title}>
            <div className={classes.RegistryBody}>
                {/* Статус-бар реестра */}
                <div className={classes.RegistryStatusBar}>
                    {regStatusInfo && (
                        <span
                            className={classes.RegistryStatusBadge}
                            style={{ background: regStatusInfo.bg, color: regStatusInfo.color }}
                        >
                            {regStatusInfo.label}
                        </span>
                    )}
                    {regDate && (
                        <span className={classes.RegistryDateText}>{formatDate(regDate)}</span>
                    )}
                    <span className={classes.RegistryIdText}>ID: {registryId}</span>
                </div>

                {/* Сводка */}
                <div className={classes.SummaryRow}>
                    <div className={classes.SummaryItem}>
                        <span className={classes.SummaryLabel}>Заявок</span>
                        <span className={classes.SummaryValue}>{requests.length}</span>
                    </div>
                    <div className={classes.SummaryItem}>
                        <span className={classes.SummaryLabel}>Сумма</span>
                        <span className={classes.SummaryValue}>{formatAmount(totalAmount)}</span>
                    </div>
                    <div className={classes.SummaryItem}>
                        <span className={classes.SummaryLabel}>Выплачено</span>
                        <span className={classes.SummaryValue + " " + classes.SuccessText}>
                            {paidRequests.length}
                        </span>
                    </div>
                    <div className={classes.SummaryItem}>
                        <span className={classes.SummaryLabel}>Выплачено на</span>
                        <span className={classes.SummaryValue}>{formatAmount(paidAmount)}</span>
                    </div>
                    <div className={classes.SummaryItem}>
                        <span className={classes.SummaryLabel} title="Ответа провайдера по этим выплатам ещё нет">
                            Ждут ответа
                        </span>
                        <span className={classes.SummaryValue}>{awaitingRequests.length}</span>
                    </div>
                    {hasFailedRequests && (
                        <div className={classes.SummaryItem}>
                            <span className={classes.SummaryLabel}>Неуспешных</span>
                            <span className={classes.SummaryValue + " " + classes.FailText}>{failedRequests.length}</span>
                        </div>
                    )}
                    {checkedIds.length > 0 && (
                        <div className={classes.SummaryItem}>
                            <span className={classes.SummaryLabel}>Выбрано</span>
                            <span className={classes.SummaryValue + " " + classes.SelectedText}>{checkedIds.length}</span>
                        </div>
                    )}
                </div>

                {/* Список заявок */}
                {loading ? (
                    <div className={classes.LoadingWrap}><Loader /></div>
                ) : (
                    <div className={classes.TableWrap}>
                        <table className={classes.Table}>
                            <thead>
                                <tr>
                                    <th className={classes.CheckboxCol}>
                                        <input
                                            type="checkbox"
                                            checked={allChecked}
                                            onChange={handleCheckAll}
                                            title="Выбрать все"
                                        />
                                    </th>
                                    <th>ФИО</th>
                                    <th>Сумма</th>
                                    <th>{isSbp ? "Телефон / банк" : "Карта"}</th>
                                    <th>Документ</th>
                                    <th>Статус</th>
                                </tr>
                            </thead>
                            <tbody>
                                {requests.map(function (r) {
                                    var requisites = isSbp
                                        ? [r.sbpPhone, r.sbpBankName].filter(Boolean).join(" · ") || "-"
                                        : r.cardPanMasked || "-";
                                    var statusName = STATUS_NAMES[Number(r.status)] || "?";
                                    var isFailed = Number(r.status) === 6;
                                    var isChecked = checkedIds.indexOf(r.id) >= 0;
                                    var docInfo = "";
                                    if (r.externalReference) docInfo += r.externalReference;
                                    if (r.externalReference && r.documentDate) docInfo += " ";
                                    if (r.documentDate) docInfo += r.documentDate;

                                    return (
                                        <tr key={r.id} className={isFailed ? classes.FailedRow : ""}>
                                            <td className={classes.CheckboxCol}>
                                                <input
                                                    type="checkbox"
                                                    checked={isChecked}
                                                    onChange={function (e) { handleCheckOne(r.id, e.target.checked); }}
                                                />
                                            </td>
                                            <td>{r.fullName || "-"}</td>
                                            <td>{formatAmount(r.amount)}</td>
                                            <td className={classes.MonoCell}>{requisites}</td>
                                            <td className={classes.DocCell}>{docInfo || "-"}</td>
                                            <td>{statusName}</td>
                                        </tr>
                                    );
                                })}
                                {requests.length === 0 && (
                                    <tr>
                                        <td colSpan="6" className={classes.EmptyRow}>Нет заявок</td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                )}

                {/* Предпросмотр отчёта: цифры до записи + подтверждение */}
                {preview && (
                    <div className={classes.UploadResult}>
                        <div className={classes.UploadResultTitle}>Что изменится, если применить отчёт</div>
                        <div className={classes.UploadResultRow}>
                            <span className={classes.SuccessText}>
                                Выплатить: <strong>{preview.success || 0}</strong>
                                {preview.successAmount ? " на " + formatAmount(Number(preview.successAmount) / 100) : ""}
                            </span>
                            <span className={classes.FailText}>
                                Отказать: <strong>{preview.failed || 0}</strong>
                                {preview.failedAmount ? " на " + formatAmount(Number(preview.failedAmount) / 100) : ""}
                            </span>
                            <span title="Строки без ответа провайдера — статус не изменится">
                                Оставить в ожидании: <strong>{preview.pending || 0}</strong>
                            </span>
                            {preview.skipped > 0 && (
                                <span title="Строки, которые применить нельзя — причина ниже">
                                    Пропустить: <strong>{preview.skipped}</strong>
                                </span>
                            )}
                        </div>
                        {preview.errors && preview.errors.length > 0 && (
                            <div className={classes.UploadErrors}>
                                {preview.errors.map(function (err, i) {
                                    return (
                                        <div key={i} className={classes.ErrorLine}>
                                            {err.transactionId ? "tx " + err.transactionId + ": " : ""}
                                            {err.error || err}
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                        <div className={classes.PreviewActions}>
                            <button
                                className={classes.BtnUpload}
                                onClick={handlePreviewApply}
                                disabled={uploading}
                            >
                                {uploading ? "Применяю..." : "Применить отчёт"}
                            </button>
                            <button
                                className={classes.BtnCancelPreview}
                                onClick={handlePreviewCancel}
                                disabled={uploading}
                            >
                                Отмена
                            </button>
                        </div>
                    </div>
                )}

                {/* Результат загрузки */}
                {uploadResult && (
                    <div className={classes.UploadResult}>
                        <div className={classes.UploadResultTitle}>Результат загрузки отчёта</div>
                        <div className={classes.UploadResultRow}>
                            <span>Обработано: <strong>{uploadResult.processed || 0}</strong></span>
                            <span className={classes.SuccessText}>Успех: <strong>{uploadResult.success || 0}</strong></span>
                            <span className={classes.FailText}>Ошибок: <strong>{uploadResult.failed || 0}</strong></span>
                            {uploadResult.pending > 0 && (
                                <span title="В отчёте нет ответа по этим выплатам — статус не менялся, ждём следующую часть отчёта">
                                    Без ответа: <strong>{uploadResult.pending}</strong>
                                </span>
                            )}
                            {uploadResult.skipped > 0 && (
                                <span title="Строки, которые не удалось понять — статус не менялся">
                                    Пропущено: <strong>{uploadResult.skipped}</strong>
                                </span>
                            )}
                        </div>
                        {uploadResult.errors && uploadResult.errors.length > 0 && (
                            <div className={classes.UploadErrors}>
                                {uploadResult.errors.map(function (err, i) {
                                    return <div key={i} className={classes.ErrorLine}>{err.id ? "#" + err.id + ": " : ""}{err.transactionId ? "tx " + err.transactionId + ": " : ""}{err.error || err}</div>;
                                })}
                            </div>
                        )}
                    </div>
                )}

                {/* Действия */}
                <div className={classes.Actions}>
                    {/* Скрытый file input */}
                    <input
                        type="file"
                        accept=".csv,.xlsx,.xls"
                        ref={fileInputRef}
                        onChange={handleFileSelected}
                        style={{ display: "none" }}
                    />

                    {!isSbp && (
                        <button
                            className={classes.BtnDownload}
                            onClick={function () { handleDownloadExcel(); }}
                            disabled={loading || requests.length === 0}
                        >
                            Скачать Excel
                        </button>
                    )}

                    <button
                        className={classes.BtnDownload}
                        onClick={function () { handleDownloadExcel("yupay"); }}
                        disabled={loading || requests.length === 0}
                    >
                        Скачать Excel (Юпэй)
                    </button>

                    {!allSuccess && (
                        <button
                            className={classes.BtnUpload}
                            onClick={handleUploadClick}
                            disabled={uploading || loading}
                        >
                            {uploading ? "Загрузка..." : "Загрузить отчёт"}
                        </button>
                    )}

                    {checkedInRegistry.length > 0 && (
                        <button
                            className={classes.BtnRemove}
                            onClick={handleRemoveFromRegistry}
                            disabled={loading || disbanding}
                        >
                            Убрать из реестра ({checkedInRegistry.length})
                        </button>
                    )}

                    {canDisband && (
                        <button
                            className={classes.BtnDisband}
                            onClick={function () { setShowDisbandConfirm(true); }}
                            disabled={loading || disbanding}
                        >
                            {disbanding ? "Расформирование..." : "Расформировать"}
                        </button>
                    )}

                    {hasFailedRequests && (
                        <button
                            className={classes.BtnRetry}
                            onClick={function () { setShowRetryModal(true); }}
                            disabled={loading || retrying}
                        >
                            Реестр из неуспешных ({failedRequests.length})
                        </button>
                    )}

                    {canSplitRemaining && (
                        <button
                            className={classes.BtnSplit}
                            onClick={function () { setShowSplitModal(true); }}
                            disabled={loading || splitting}
                        >
                            Остаток в новый реестр ({remainingRequests.length})
                        </button>
                    )}
                </div>

                {/* Подтверждение удаления из реестра */}
                {showRemoveConfirm && (
                    <div className={classes.RetryOverlay}>
                        <div className={classes.RetryModal}>
                            <div className={classes.RetryTitle}>
                                Убрать из реестра?
                            </div>
                            <div className={classes.DisbandText}>
                                {checkedInRegistry.length} заявок будут возвращены в статус "Ожидает выплаты".
                            </div>
                            <div className={classes.RetryActions}>
                                <button
                                    className={classes.BtnRetryCancel}
                                    onClick={function () { setShowRemoveConfirm(false); }}
                                >
                                    Отмена
                                </button>
                                <button
                                    className={classes.BtnDisbandConfirm}
                                    onClick={handleRemoveConfirmed}
                                    disabled={loading}
                                >
                                    Убрать
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* Подтверждение расформирования */}
                {showDisbandConfirm && (
                    <div className={classes.RetryOverlay}>
                        <div className={classes.RetryModal}>
                            <div className={classes.RetryTitle}>
                                Расформировать реестр?
                            </div>
                            <div className={classes.DisbandText}>
                                Все {inRegistryRequests.length} заявок из реестра будут возвращены в статус "Ожидает выплаты". Реестр будет удалён. Это действие нельзя отменить.
                            </div>
                            <div className={classes.RetryActions}>
                                <button
                                    className={classes.BtnRetryCancel}
                                    onClick={function () { setShowDisbandConfirm(false); }}
                                >
                                    Отмена
                                </button>
                                <button
                                    className={classes.BtnDisbandConfirm}
                                    onClick={handleDisbandRegistry}
                                    disabled={disbanding}
                                >
                                    {disbanding ? "Расформирование..." : "Расформировать"}
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* Модалка для названия нового реестра из неуспешных */}
                {showRetryModal && (
                    <div className={classes.RetryOverlay}>
                        <div className={classes.RetryModal}>
                            <div className={classes.RetryTitle}>
                                Создать реестр из {failedRequests.length} неуспешных заявок
                            </div>
                            <input
                                className={classes.RetryInput}
                                placeholder="Название нового реестра..."
                                value={retryRegistryName}
                                onChange={function (e) { setRetryRegistryName(e.target.value); }}
                                autoFocus
                            />
                            <div className={classes.RetryActions}>
                                <button
                                    className={classes.BtnRetryCancel}
                                    onClick={function () { setShowRetryModal(false); }}
                                >
                                    Отмена
                                </button>
                                <button
                                    className={classes.BtnRetryConfirm}
                                    onClick={handleRetryFailed}
                                    disabled={retrying || !retryRegistryName}
                                >
                                    {retrying ? "Создание..." : "Создать"}
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* Модалка для остатка в новый реестр */}
                {showSplitModal && (
                    <div className={classes.RetryOverlay}>
                        <div className={classes.RetryModal}>
                            <div className={classes.RetryTitle}>
                                Перенести {remainingRequests.length} незавершённых заявок в новый реестр
                            </div>
                            <input
                                className={classes.RetryInput}
                                placeholder="Название нового реестра..."
                                value={splitRegistryName}
                                onChange={function (e) { setSplitRegistryName(e.target.value); }}
                                autoFocus
                            />
                            <div className={classes.RetryActions}>
                                <button
                                    className={classes.BtnRetryCancel}
                                    onClick={function () { setShowSplitModal(false); }}
                                >
                                    Отмена
                                </button>
                                <button
                                    className={classes.BtnRetryConfirm}
                                    onClick={handleSplitRemaining}
                                    disabled={splitting || !splitRegistryName}
                                >
                                    {splitting ? "Перенос..." : "Перенести"}
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </Modal>
    );
}

CrmRegistryModal.defaultProps = {
    onClose: function () {},
    onComplete: function () {},
    onMessage: function () {},
    registryInfo: null,
    payoutType: "card",
};

export default CrmRegistryModal;



// WEBPACK FOOTER //
// ./src/Components/ComponentsOfAdministrator/CrmRegistryModal/index.js